import { Box, Button, Image, Text } from '@betty/moonlight';
import { memo, useCallback, useMemo, useState } from 'react';
import Config from '@betty/toolkit/config';
import { Linking } from 'react-native';
import Clipboard from '@react-native-clipboard/clipboard';
import { RefferalPromoSummaryProps } from './ReferrerPromoSummary.types';
import { useTranslation } from 'react-i18next';

function RefferalPromoSummary({
  refferalCode,
  title,
  shortDescription,
}: RefferalPromoSummaryProps) {
  const [t] = useTranslation('promos');
  const [linkCopied, setLinkCopied] = useState(false);
  const link = useMemo(
    () => `${Config.PLATFORM_URL}/join/${refferalCode}`,
    [refferalCode],
  );

  const shareText = useMemo(() => `${t('shareText')} ${link}`, [link, t]);

  const copyToClipboard = useCallback(() => {
    Clipboard.setString(link);
    setLinkCopied(true);
  }, [link]);

  const shareViaSMS = useCallback(() => {
    Linking.openURL(encodeURI(`sms:?&body=${shareText}`));
  }, [shareText]);

  const shareViaEmail = useCallback(() => {
    Linking.openURL(
      encodeURI(
        `mailto:support@betty.ca?subject=Play Betty with me! 🎰💛&body=${shareText}`,
      ),
    );
  }, [shareText]);

  const shareViaFacebook = useCallback(() => {
    Linking.openURL(
      `https://www.facebook.com/sharer.php?u=${encodeURIComponent(link)}`,
    );
  }, [link]);

  const shareViaTwitter = useCallback(() => {
    Linking.openURL(`https://twitter.com/intent/tweet?text=${shareText}`);
  }, [shareText]);

  return (
    <Box gap="space-300">
      <Text variant="h4" gradient="darkSurfaceAccent">
        {title}
      </Text>
      <Text variant="body-m" color="darkSurfacePrimary">
        {shortDescription}
      </Text>
      <Box position="relative">
        <Text
          variant="body-m"
          color="darkSurfacePrimary"
          backgroundColor="secondaryBackgroundDefault"
          textAlign="center"
          padding="space-400"
          borderRadius="radius-200"
        >
          {link}
        </Text>
        {linkCopied ? (
          <Image
            name="common.check"
            position="absolute"
            top="space-400"
            right="space-200"
            width={20}
            height={20}
          />
        ) : null}
      </Box>
      <Button
        variant="secondary"
        locNS="promos"
        locKey="copyLink"
        onPress={copyToClipboard}
        alignSelf="center"
        width="100%"
      />
      <Text
        variant="body-m-semi"
        color="darkSurfacePrimary"
        textAlign="center"
        locNS="promos"
        locKey="or"
      />
      <Button
        variant="secondary"
        locNS="promos"
        locKey="smsShare"
        onPress={shareViaSMS}
        alignSelf="center"
        width="100%"
      />
      <Button
        variant="secondary"
        locNS="promos"
        locKey="emailShare"
        onPress={shareViaEmail}
        alignSelf="center"
        width="100%"
      />
      <Button
        variant="secondary"
        locNS="promos"
        locKey="facebookShare"
        onPress={shareViaFacebook}
        alignSelf="center"
        width="100%"
      />
      <Button
        variant="secondary"
        locNS="promos"
        locKey="twitterShare"
        onPress={shareViaTwitter}
        alignSelf="center"
        width="100%"
      />
    </Box>
  );
}

export default memo(RefferalPromoSummary);
